/** Shared image format helpers (wallpapers + day attachments). */

export const IMAGE_EXTENSIONS = [
  'jpg',
  'jpeg',
  'png',
  'webp',
  'bmp',
  'gif',
  'avif',
  'heic',
  'heif',
] as const;

export const IMAGE_ACCEPT =
  'image/jpeg,image/jpg,image/png,image/webp,image/bmp,image/gif,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.bmp,.gif,.avif,.heic,.heif';

const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  bmp: 'image/bmp',
  gif: 'image/gif',
  avif: 'image/avif',
  heic: 'image/heic',
  heif: 'image/heif',
};

const SUPPORTED_MIME = new Set([
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
  'image/bmp',
  'image/gif',
  'image/avif',
  'image/heic',
  'image/heif',
  'image/x-ms-bmp',
]);

export function mimeFromFileName(name: string): string | null {
  const ext = name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXT[ext] ?? null;
}

export function isSupportedImageFile(file: File): boolean {
  if (file.type && SUPPORTED_MIME.has(file.type.toLowerCase())) return true;
  if (file.type.startsWith('image/')) return true;
  return Boolean(mimeFromFileName(file.name));
}

export function extensionForImageMime(mime: string, fileName?: string): string {
  const lower = mime.toLowerCase();
  if (lower.includes('webp')) return 'webp';
  if (lower.includes('png')) return 'png';
  if (lower.includes('gif')) return 'gif';
  if (lower.includes('bmp')) return 'bmp';
  if (lower.includes('avif')) return 'avif';
  if (lower.includes('heic')) return 'heic';
  if (lower.includes('heif')) return 'heif';
  if (lower.includes('jpeg') || lower.includes('jpg')) return 'jpg';
  const fromName = fileName?.split('.').pop()?.toLowerCase();
  if (fromName && IMAGE_EXTENSIONS.includes(fromName as (typeof IMAGE_EXTENSIONS)[number])) {
    return fromName;
  }
  return 'jpg';
}

export function normalizeImageMime(file: File): string {
  if (file.type && SUPPORTED_MIME.has(file.type.toLowerCase())) {
    return file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  }
  return mimeFromFileName(file.name) ?? 'image/jpeg';
}
